import * as Bluebird from 'bluebird';
import { FormSectionHandler } from '../entity.handler';
import {  DeviceResponse, DeviceAccessory, DeviceTypeAccessory } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { cloneDeep } from 'lodash';
import { EntityUtils, ComboField } from '../utils';

interface EditorValues {
    [key: string]: any;
}

export interface DeviceAccessoryResponse extends Pick<DeviceResponse, 'id' | 'owner' | 'entity' | 'accessories'> {}

export class AccessorySectionHandler implements FormSectionHandler<DeviceAccessoryResponse, EditorValues> {

    type = 'form';
    icon = 'cog'; 
    bulk = true;
    hidden = false;
    warning = '';

    title = this.accessory.name;
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public accessoryId: string,
        public accessory: DeviceTypeAccessory,
        private applyUpdateCallback: (entity: any, payload: any) => Promise<DeviceAccessoryResponse>, 
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICE.ACCESSORIES.${key}`, values);
    }

    async applyUpdate(entity: any, payload: any): Promise<DeviceAccessoryResponse> {
        // The payload only contains items the user has changed.
        // We must ensure that the payload includes any existing settings for this accessory.
        const payloadAccessory = payload?.accessories[this.accessoryId];
        if (payloadAccessory) {
            payloadAccessory.settings = {
                ...entity?.accessories?.[this.accessoryId]?.settings,
                ...payloadAccessory?.settings,
            };
        }

        const shouldUploadTask = entity?.asset && this.accessory?.features?.upload_settings;
        if (shouldUploadTask) {
            // create a task if the accessory supports settings upload.
            try {
                const taskAccessory = [{
                    id: this.accessoryId,
                    ...payloadAccessory
                }];
                await this.admin.applyUpdate(api => api.tasks.createTask({
                    ownerId: this.ownerId,
                    options: {
                        retries: 3,
                        delay: 1,
                        timeout: 5,
                    },
                    type: 'DeviceUploadTask',
                    data: {
                        asset: {
                            id: entity.asset.id,
                            name: entity.asset.name,
                        },
                        accessories: taskAccessory
                    },
                }));
            } catch (err) {
                // swallow errors to ensure that we at least save the updated settings.
                console.error(err);
                this.warning = this.translate('UPLOAD_FAILURE');
            }
        }
        return this.applyUpdateCallback(entity, payload);
    }

    async mapEntityToEditor(entity: DeviceAccessoryResponse): Promise<EditorValues> {
        const acc = entity.accessories && entity.accessories[this.accessoryId];
        if (acc) {
            return {
                enabled: 'true',
                ...acc.settings,
            };
        } else {
            return { enabled: 'false' };
        }
        
    }

    mapEditorToEntity(entity: DeviceAccessoryResponse, values: EditorValues): Promise<DeviceAccessoryResponse> {
        if (values) {
            entity.accessories = entity.accessories || {};
            if (values.enabled === 'false') {
                entity.accessories[this.accessoryId] = null;
            } else {
                const acc: DeviceAccessory = entity.accessories[this.accessoryId] || { name: this.accessory.name, settings: {} };
                acc.settings = {
                    ...acc.settings,
                    ...values,
                };
                entity.accessories[this.accessoryId] = acc;
            }
        }
        return Promise.resolve(entity);
    }


    async getFields(entity: DeviceAccessoryResponse): Promise<FormBuilderField[]> {


        const parseFields = (fields: FormBuilderField[]): Bluebird<FormBuilderField[]> => {

            return Bluebird.map(fields, async field => {
                // TODO: add any field translation and parsing here in future
                field.getValue = (f: FormBuilderField, values: any): any => {
                    const value = values[f.id];
                    return value;
                };
                return field;
            });
        };

        let subfields = cloneDeep(JSON.parse(this.accessory.settingsDefinition));
        subfields = await parseFields(subfields && subfields.fields || []);

        const results = [
            ComboField('enabled', this.translate('FIELDS.ENABLED'), true, [
                { key: 'false', value: this.translate('FIELDS.NO') },
                { key: 'true', value: this.translate('FIELDS.YES'), fields: subfields },
            ]),
        ];

        return EntityUtils.prepareBulk(!entity, results.map(x => x.id), results);

    }

}
